import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation } from "wouter";
import { toast } from "sonner";
import {
  useCreateTask,
  useListSpaceResources,
  useListUsers,
  getListUsersQueryKey,
} from "@workspace/api-client-react";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MemberCombobox } from "@/components/operations/member-combobox";
import { ClipboardList } from "lucide-react";

// Phase 41 Plan 09 — Task-01/D1: ONE create form, ONE type switcher at the top.
// The 20-second walk-in quick-create form is Phase 51 — this is the only
// intake surface this phase. Per-type required fields mirror
// artifacts/api-server/src/routes/tasks.ts's own superRefine EXACTLY (see
// that file's `CreateTaskBody` superRefine block) so the client guard never
// disagrees with the server:
//   complaint / request / feedback_followup -> accountId required
//   assignment                              -> assigneeId required
//   operational                             -> unconditionally rejected (Phase 43)
//   maintenance                             -> no extra requirement (vendor fields optional)
// zoneLabel is required on EVERY type (41-07-SUMMARY.md key-decision: the DB column is
// NOT NULL for all tasks, not just complaint) — so the zone picker is NOT type-conditional.

const TASK_TYPES = [
  "complaint",
  "request",
  "maintenance",
  "assignment",
  "feedback_followup",
  "operational",
] as const;
type TaskTypeValue = (typeof TASK_TYPES)[number];

const PRIORITIES = ["P1", "P2", "P3", "P4"] as const;

const TASK_TYPE_OPTIONS: { value: TaskTypeValue; label: string; hint: string }[] = [
  { value: "complaint", label: "Complaint", hint: "A member issue — tied to an account and a zone" },
  { value: "request", label: "Request", hint: "A member ask — tied to an account" },
  { value: "maintenance", label: "Maintenance", hint: "A repair/upkeep job — account optional, vendor optional" },
  { value: "assignment", label: "Assignment", hint: "A chore handed to one specific person" },
  { value: "feedback_followup", label: "Feedback follow-up", hint: "Following up on a member's feedback" },
  { value: "operational", label: "Operational (recurring checklist)", hint: "Coming in Phase 43 — not available yet" },
];

// Non-bookable zones are a seeded label list (41-CONTEXT.md "Zones") — these were never
// modeled as space_resources and must not be, or the booking/pricing domain gets
// contaminated. Bookable zones are read LIVE below via useListSpaceResources — nothing
// here is hardcoded as a fake "Suite 7 / Conference Room / Podcast Studio" placeholder.
const NON_BOOKABLE_ZONES = ["Pantry", "Washroom M", "Washroom F", "Reception", "Workstation Zone B"];

const CreateTaskForm = z
  .object({
    type: z.enum(TASK_TYPES),
    title: z.string().min(1, "Title is required"),
    description: z.string().optional(),
    accountId: z.number().nullable().optional(),
    zoneResourceId: z.number().nullable().optional(),
    zoneLabel: z.string().min(1, "Zone is required"),
    priority: z.enum(PRIORITIES),
    assigneeId: z.number().nullable().optional(),
    assignedRole: z.string().optional(),
    vendorName: z.string().optional(),
    vendorExpectedAt: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      (data.type === "complaint" || data.type === "request" || data.type === "feedback_followup") &&
      data.accountId == null
    ) {
      ctx.addIssue({ code: "custom", path: ["accountId"], message: "Account is required for this task type" });
    }
    if (data.type === "assignment" && data.assigneeId == null) {
      ctx.addIssue({ code: "custom", path: ["assigneeId"], message: "Assignee is required for assignment tasks" });
    }
    if (data.type === "operational") {
      ctx.addIssue({ code: "custom", path: ["type"], message: "Operational tasks aren't available until Phase 43" });
    }
  });

type CreateValues = z.infer<typeof CreateTaskForm>;

const EMPTY_CREATE: CreateValues = {
  type: "complaint",
  title: "",
  description: "",
  accountId: null,
  zoneResourceId: null,
  zoneLabel: "",
  priority: "P3",
  assigneeId: null,
  assignedRole: "",
  vendorName: "",
  vendorExpectedAt: "",
};

export default function TaskCreatePage() {
  const [, navigate] = useLocation();
  const createMutation = useCreateTask();

  const { data: resources } = useListSpaceResources();
  // TASK-01/assignment: admin-only route server-side (users.ts requireAdmin) — non-admin
  // staff creating an "assignment" task fall back to the assignedRole (pool) field below;
  // this is a graceful client-side degradation, not a hidden permission (server remains
  // authoritative on every mutating route).
  const { data: users, isError: usersUnavailable } = useListUsers({
    query: { retry: false, queryKey: getListUsersQueryKey() },
  });

  const form = useForm<CreateValues>({
    resolver: zodResolver(CreateTaskForm),
    defaultValues: EMPTY_CREATE,
  });

  const type = form.watch("type");
  const zoneResourceId = form.watch("zoneResourceId");
  const zoneLabel = form.watch("zoneLabel");

  const accountRequired = type === "complaint" || type === "request" || type === "feedback_followup";
  const isOperational = type === "operational";

  const zoneValue =
    zoneResourceId != null ? `resource:${zoneResourceId}` : zoneLabel ? `label:${zoneLabel}` : "";

  function onZoneChange(value: string) {
    if (value.startsWith("resource:")) {
      const id = Number(value.slice("resource:".length));
      const res = resources?.find((r) => r.id === id);
      form.setValue("zoneResourceId", id, { shouldValidate: true });
      form.setValue("zoneLabel", res?.label ?? "", { shouldValidate: true });
    } else if (value.startsWith("label:")) {
      form.setValue("zoneResourceId", null, { shouldValidate: true });
      form.setValue("zoneLabel", value.slice("label:".length), { shouldValidate: true });
    }
  }

  async function onSubmit(values: CreateValues) {
    try {
      const created = await createMutation.mutateAsync({
        data: {
          type: values.type,
          title: values.title,
          description: values.description?.trim() || null,
          accountId: values.accountId ?? null,
          zoneResourceId: values.zoneResourceId ?? null,
          zoneLabel: values.zoneLabel,
          // This is the staff-facing CRM create form — WhatsApp/QR/voice intake channels
          // are Phase 50's bot entry points, out of scope here.
          channel: "staff",
          priority: values.priority,
          assigneeId: values.assigneeId ?? null,
          assignedRole: values.assignedRole?.trim() || null,
          vendorName: values.type === "maintenance" ? values.vendorName?.trim() || null : null,
          vendorExpectedAt:
            values.type === "maintenance" && values.vendorExpectedAt
              ? new Date(values.vendorExpectedAt).toISOString()
              : null,
        },
      });
      toast.success(`Task ${created.taskNumber} created.`);
      navigate(`/tasks/${created.id}`);
    } catch (err) {
      const data = (err as { data?: { message?: string; error?: string } })?.data;
      toast.error(data?.message ?? data?.error ?? "Could not create task. Please try again.");
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex items-center gap-2">
        <ClipboardList className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">New Task</h1>
          <p className="text-sm text-muted-foreground">
            One form, every task type. Pick a type below to reveal its fields.
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Task type</CardTitle>
              <CardDescription>Choosing the type reveals only that type's fields.</CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                name="type"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TASK_TYPE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {TASK_TYPE_OPTIONS.find((o) => o.value === field.value)?.hint}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {isOperational ? (
            <Alert>
              <AlertDescription>
                Operational (recurring checklist) tasks are seeded by <code>task_template</code>, which
                launches in Phase 43. Pick another type above to continue.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    name="title"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. AC not cooling in Suite 7" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="description"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Optional — extra detail for whoever picks this up" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="priority"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Priority</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="max-w-[160px]"><SelectValue /></SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {PRIORITIES.map((p) => (
                              <SelectItem key={p} value={p}>{p}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Where</CardTitle>
                  <CardDescription>
                    Bookable zones are read live from the floor plan; other zones come from a fixed list.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <FormField
                    name="zoneLabel"
                    control={form.control}
                    render={() => (
                      <FormItem>
                        <FormLabel>Zone <span className="text-destructive">*</span></FormLabel>
                        <Select value={zoneValue} onValueChange={onZoneChange}>
                          <FormControl>
                            <SelectTrigger><SelectValue placeholder="Pick a zone" /></SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectGroup>
                              <SelectLabel>Bookable (floor plan)</SelectLabel>
                              {(resources ?? []).map((r) => (
                                <SelectItem key={`resource:${r.id}`} value={`resource:${r.id}`}>
                                  {r.label}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                            <SelectGroup>
                              <SelectLabel>Other zones</SelectLabel>
                              {NON_BOOKABLE_ZONES.map((label) => (
                                <SelectItem key={`label:${label}`} value={`label:${label}`}>
                                  {label}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {(accountRequired || type === "maintenance" || type === "assignment") && (
                <Card>
                  <CardHeader>
                    <CardTitle>Who / what this is about</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {(accountRequired || type === "maintenance") && (
                      <FormField
                        name="accountId"
                        control={form.control}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Account {accountRequired ? <span className="text-destructive">*</span> : "(optional)"}
                            </FormLabel>
                            <FormControl>
                              <MemberCombobox value={field.value ?? null} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {type === "maintenance" && (
                      <>
                        <FormField
                          name="vendorName"
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Vendor (optional)</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g. CoolAir Services" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          name="vendorExpectedAt"
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Vendor expected at (optional)</FormLabel>
                              <FormControl>
                                <Input type="datetime-local" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </>
                    )}

                    {type === "assignment" && (
                      <>
                        <FormField
                          name="assigneeId"
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Assignee <span className="text-destructive">*</span></FormLabel>
                              {usersUnavailable ? (
                                <Alert variant="destructive">
                                  <AlertDescription>
                                    Team member list needs an admin login — ask an admin to create this
                                    assignment, or set a target role below instead.
                                  </AlertDescription>
                                </Alert>
                              ) : (
                                <Select
                                  value={field.value != null ? String(field.value) : ""}
                                  onValueChange={(v) => field.onChange(Number(v))}
                                >
                                  <FormControl>
                                    <SelectTrigger><SelectValue placeholder="Pick a team member" /></SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {(users ?? []).map((u) => (
                                      <SelectItem key={u.id} value={String(u.id)}>
                                        {u.name} · {u.role.replace(/_/g, " ")}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          name="assignedRole"
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Role pool (optional)</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g. housekeeping" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}

          <div className="flex justify-end gap-2">
            <Button type="submit" disabled={createMutation.isPending || isOperational}>
              {createMutation.isPending ? "Creating…" : "Create Task"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
